import { keepPreviousData, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { AgentRevision } from "@/api/generated/AgentRevision"
import type { AgentSummary } from "@/api/generated/AgentSummary"
import type { AgentTag } from "@/api/generated/AgentTag"
import type { BudgetAlert } from "@/api/generated/BudgetAlert"
import type { BudgetPolicy } from "@/api/generated/BudgetPolicy"
import type { BudgetStatus } from "@/api/generated/BudgetStatus"
import type { Overrides } from "@/api/generated/Overrides"
import type { SendResult } from "@/api/generated/SendResult"
import { useApiMutation } from "@/api/mutation"
import { mcpKeys } from "@/api/queries/mcp"
import { modelKeys } from "@/api/queries/providers"
import { skillKeys } from "@/api/queries/skills"

export const REASONING_EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const
export type ReasoningEffort = (typeof REASONING_EFFORTS)[number]
export const CACHE_TTLS = ["5m", "1h"] as const

export interface AgentListFilters {
  include_archived?: boolean
  tag_id?: number
  q?: string
}

export const agentKeys = {
  all: ["agents"] as const,
  lists: () => [...agentKeys.all, "list"] as const,
  list: (f: AgentListFilters) => [...agentKeys.lists(), f] as const,
  detail: (id: number) => [...agentKeys.all, "detail", id] as const,
  revisions: (id: number) => [...agentKeys.all, "revisions", id] as const,
  tags: ["agent-tags"] as const,
}

export const budgetKeys = {
  all: ["budgets"] as const,
  list: () => [...budgetKeys.all, "list"] as const,
  status: () => [...budgetKeys.all, "status"] as const,
  alerts: () => [...budgetKeys.all, "alerts"] as const,
}

// ------------------------------------------------------------------ agents

export const agentsQuery = (f: AgentListFilters = {}) =>
  queryOptions({
    queryKey: agentKeys.list(f),
    queryFn: ({ signal }) => api.get<ListResponse<AgentSummary>>("/agents", { signal, query: { ...f } }),
    select: (r) => r.items,
    placeholderData: keepPreviousData,
  })

export const useAgents = (f: AgentListFilters = {}) => useQuery(agentsQuery(f))

export const agentQuery = (id: number) =>
  queryOptions({
    queryKey: agentKeys.detail(id),
    queryFn: ({ signal }) => api.get<AgentDetail>(`/agents/${id}`, { signal }),
  })

export const useAgent = (id: number) => useQuery(agentQuery(id))

export const useAgentRevisions = (id: number, enabled = true) =>
  useQuery({
    queryKey: agentKeys.revisions(id),
    queryFn: ({ signal }) => api.get<ListResponse<AgentRevision>>(`/agents/${id}/revisions`, { signal }),
    select: (r) => r.items,
    enabled,
  })

export interface McpAssignmentInput {
  mcp_server_id: number
  disabled_tools: string[]
}

export interface AgentInput {
  name?: string
  description?: string
  icon?: string
  model_id?: number
  system_prompt?: string
  temperature?: number
  top_p?: number | null
  top_k?: number | null
  max_tokens?: number | null
  thinking_enabled?: boolean
  reasoning_effort?: ReasoningEffort
  prompt_cache_static?: string
  prompt_cache_messages?: string
  max_steps?: number
  allow_as_chat_agent?: boolean
  tag_ids?: number[]
  skill_ids?: number[]
  mcp_servers?: McpAssignmentInput[]
  revision_note?: string
}

/** Everything that shows agent counts elsewhere (models, skills, MCP, budgets). */
const agentDependents = [agentKeys.all, modelKeys.all, skillKeys.all, mcpKeys.all, agentKeys.tags]

export function useCreateAgent() {
  const { t } = useTranslation()
  return useApiMutation<AgentDetail, AgentInput>({
    mutationFn: (body) => api.post("/agents", body),
    invalidates: agentDependents,
    successToast: (a) => t("studio.agents.created", { name: a.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateAgent(id: number) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<AgentDetail, AgentInput>({
    mutationFn: (body) => api.patch(`/agents/${id}`, body),
    onSuccess: (a) => qc.setQueryData(agentKeys.detail(id), a),
    invalidates: [...agentDependents, agentKeys.revisions(id)],
    successToast: (a) => t("studio.agents.saved", { name: a.name }),
    errorToast: "unlessFields",
  })
}

export function useDuplicateAgent() {
  const { t } = useTranslation()
  return useApiMutation<AgentDetail, Pick<AgentSummary, "id" | "name">>({
    mutationFn: (a) => api.post(`/agents/${a.id}/duplicate`),
    invalidates: agentDependents,
    successToast: (a) => t("studio.agents.duplicated", { name: a.name }),
  })
}

export function useArchiveAgent() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<AgentDetail, { id: number; name: string; archive: boolean }>({
    mutationFn: ({ id, archive }) => api.post(`/agents/${id}/${archive ? "archive" : "unarchive"}`),
    onSuccess: (a) => qc.setQueryData(agentKeys.detail(a.id), a),
    invalidates: [agentKeys.all],
    successToast: (a, v) =>
      v.archive
        ? t("studio.agents.archived", { name: a.name })
        : t("studio.agents.unarchived", { name: a.name }),
  })
}

/** Delete; a 409 (agent is used by teams) is surfaced by the caller. */
export function useDeleteAgent() {
  const { t } = useTranslation()
  return useApiMutation<void, { id: number; name: string }>({
    mutationFn: (a) => api.delete(`/agents/${a.id}`),
    invalidates: agentDependents,
    successToast: (_d, a) => t("studio.agents.deleted", { name: a.name }),
    errorToast: false,
  })
}

export function useRestoreRevision(agentId: number) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<AgentDetail, AgentRevision>({
    mutationFn: (r) => api.post(`/agents/${agentId}/revisions/${r.id}/restore`),
    onSuccess: (a) => qc.setQueryData(agentKeys.detail(agentId), a),
    invalidates: [agentKeys.all, modelKeys.all],
    successToast: () => t("studio.agents.revisions.restored"),
  })
}

export interface SendOnceInput {
  agent_id: number
  message: string
  context_id?: string
  overrides?: Overrides
}

/** Synchronous one-shot turn (`POST /chat/send`), used by the agent playground. */
export function useSendOnce() {
  return useApiMutation<SendResult, SendOnceInput & { signal?: AbortSignal }>({
    mutationFn: ({ signal, ...body }) => api.post("/chat/send", body, { signal }),
    errorToast: false,
  })
}

// ------------------------------------------------------------------ tags

export const tagsQuery = () =>
  queryOptions({
    queryKey: agentKeys.tags,
    queryFn: ({ signal }) => api.get<ListResponse<AgentTag>>("/agent-tags", { signal }),
    select: (r) => r.items,
  })

export const useAgentTags = () => useQuery(tagsQuery())

export function useCreateTag() {
  return useApiMutation<AgentTag, { name: string; color: string }>({
    mutationFn: (body) => api.post("/agent-tags", body),
    invalidates: [agentKeys.tags],
    errorToast: "unlessFields",
  })
}

export function useUpdateTag() {
  return useApiMutation<AgentTag, { id: number; name?: string; color?: string }>({
    mutationFn: ({ id, ...body }) => api.patch(`/agent-tags/${id}`, body),
    invalidates: [agentKeys.tags],
    errorToast: "unlessFields",
  })
}

export function useDeleteTag() {
  const { t } = useTranslation()
  return useApiMutation<void, AgentTag>({
    mutationFn: (tag) => api.delete(`/agent-tags/${tag.id}`),
    invalidates: [agentKeys.tags, agentKeys.all],
    successToast: (_d, tag) => t("studio.tags.deleted", { name: tag.name }),
  })
}

// ------------------------------------------------------------------ budgets

export const useBudgets = () =>
  useQuery({
    queryKey: budgetKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<BudgetPolicy>>("/budgets", { signal }),
    select: (r) => r.items,
  })

export const budgetStatusQuery = () =>
  queryOptions({
    queryKey: budgetKeys.status(),
    queryFn: ({ signal }) => api.get<ListResponse<BudgetStatus>>("/budgets/status", { signal }),
    select: (r) => r.items,
  })

export const useBudgetStatus = () => useQuery(budgetStatusQuery())

export const useBudgetAlerts = () =>
  useQuery({
    queryKey: budgetKeys.alerts(),
    queryFn: ({ signal }) => api.get<ListResponse<BudgetAlert>>("/budgets/alerts", { signal }),
    select: (r) => r.items,
  })

export interface BudgetInput {
  name?: string
  scope?: "tenant" | "agent"
  agent_id?: number | null
  period?: "day" | "week" | "month"
  max_spend?: number
  enabled?: boolean
  action?: "block" | "warn"
  alert_threshold_pct?: number
}

export function useCreateBudget() {
  const { t } = useTranslation()
  return useApiMutation<BudgetPolicy, BudgetInput>({
    mutationFn: (body) => api.post("/budgets", body),
    invalidates: [budgetKeys.all],
    successToast: () => t("studio.budgets.created"),
    errorToast: "unlessFields",
  })
}

export function useUpdateBudget() {
  const { t } = useTranslation()
  return useApiMutation<BudgetPolicy, { id: number } & BudgetInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/budgets/${id}`, body),
    invalidates: [budgetKeys.all],
    successToast: () => t("studio.budgets.updated"),
    errorToast: "unlessFields",
  })
}

export function useDeleteBudget() {
  const { t } = useTranslation()
  return useApiMutation<void, BudgetPolicy>({
    mutationFn: (b) => api.delete(`/budgets/${b.id}`),
    invalidates: [budgetKeys.all],
    successToast: () => t("studio.budgets.deleted"),
  })
}
